// Deal keepers: the predicates and the contract constants.
//
// KEEPERS ONLY. `Deal`, `DealCreate`, `DealUpdate` and `DealState` are
// generated (src/gen/client.ts) and re-exported by src/facade.ts. What stays
// here is what the document does not carry: two predicates that name a question
// the SPA asks of a row, the closed-won set the pipeline report reasons about,
// the sort-field list a table header maps to, and the country-code scalar the
// deal form validates one field against.
import { z } from "zod";
/**
 * States whose revenue is contracted rather than probability-weighted. A deal
 * being built is as signed as one already running, so `implementation` counts
 * here alongside won and live, and so does `ended`: it was won, and its issued
 * history is still contracted money.
 */
export const CLOSED_WON_STATES = [
    "won",
    "implementation",
    "live",
    "ended",
];
/**
 * ISO-3166-1 alpha-2. A CODE, not a name, and the regex is the whole of the
 * validation: "ca", "CAN" and "Canada" are each a different string to a GROUP
 * BY, so accepting them would let one country occupy three rows of the
 * revenue-by-region report and none of them reconcile.
 *
 * No list of the ~250 legal codes: keeping one in step with the world is work
 * with no reader, and a wrong-but-well-formed code is a data-entry mistake a
 * report makes visible, not a class of bug a schema can close.
 */
export const CountryCode = z
    .string()
    .regex(/^[A-Z]{2}$/, "expected an ISO-3166-1 alpha-2 country code, e.g. CA or US");
/**
 * Plottable: this deal has a point a map can draw.
 *
 * Generic in the row type so the narrowing KEEPS the row. Written as a concrete
 * `Pick` it narrowed to the Pick itself, so `deals.filter(dealHasCoordinates)`
 * handed back something with a plottable coordinate and nothing else on it —
 * no id, no customer_name — and every caller that wanted to draw a pin had to
 * re-assert the type it started with.
 */
export function dealHasCoordinates(deal) {
    return deal.customer_lat !== null && deal.customer_lng !== null;
}
/**
 * Located: this deal contributes to a real bucket of the revenue-by-region
 * report rather than to its unlocated one.
 *
 * Deliberately NOT the same question as `dealHasCoordinates`. A deal can be
 * placed in a country and still have no point to plot, which is the ordinary
 * state of a deal somebody has filled in properly but not precisely — the
 * report can group it, the map cannot draw it, and the two filters on the deals
 * list exist so each of those worklists can be found.
 */
export function dealHasLocation(deal) {
    return deal.customer_country !== null;
}
/** True when this deal's terms come from an agreement it does not itself pin. */
export function dealInheritsTerms(deal) {
    return deal.agreement_id === null && deal.effective_agreement_id !== null;
}
/**
 * The sortable columns of the deals list, in the order the table draws them.
 *
 * The document declares the same set as the `sort` parameter's enum
 * (`customer_name:asc`, …). This list is the bare field names the SPA's table
 * headers key on, which is a different shape, not a second source of truth.
 */
export const DEAL_SORT_FIELDS = [
    "customer_name",
    "state",
    "win_prob",
    "start_date_earliest",
    "start_date_latest",
    "renewal_date",
    "closed_on",
    // Sortable, so a list can be read geographically. The coordinates are not:
    // "order by latitude" is not a question anyone asks of a table, and the map
    // is where that data is meant to be read.
    "customer_country",
    "customer_region",
    "customer_city",
    // Sortable so the deals list can be read biggest-customer-first, which is the
    // order the implementation-fee bands are interesting in.
    "customer_account_count",
    "created_at",
    "updated_at",
];
//# sourceMappingURL=deal.js.map